<template>
  <!-- LiteApp: 使用 scroll-view 组件 -->
  <scroll-view
    v-if="isLiteApp"
    ref="scrollViewRef"
    v-bind="scrollViewAttrs"
    v-on="scrollViewListeners"
  >
    <slot />
  </scroll-view>
  <!-- H5: 使用 div 元素 -->
  <div
    v-else
    ref="scrollRef"
    v-bind="divAttrs"
    :class="scrollClass"
    :style="scrollStyle"
    :data-show-scrollbar="showScrollbar"
  >
    <slot />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'virtual-vue';
import platform, { PlatformEnum } from '@tencent/wphk-platform-adapter';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import type { ScrollToOptions, ScrollByOptions } from './scroll-view';

const { name, n, classes } = createNamespace('scroll-view');

export default defineComponent({
  name,
  inheritAttrs: false,
  props,
  emits,
  setup(props, { emit, attrs }) {
    const scrollRef = ref<HTMLElement | null>(null);
    const scrollViewRef = ref<any>(null);

    // 平台检测
    const isLiteApp = computed(() => platform.platformType === PlatformEnum.LiteApp);

    // 处理用户自定义样式
    const userStyle = computed(() => {
      if (typeof props.style === 'string') {
        return props.style;
      }
      if (typeof props.style === 'object' && props.style !== null) {
        return props.style;
      }
      return {};
    });

    // LiteApp: 直接透传 scroll-view 属性
    const scrollViewAttrs = computed(() => {
      if (!isLiteApp.value) return {};

      const result: Record<string, any> = {
        'scroll-x': props.scrollX,
        'scroll-y': props.scrollY,
        'scroll-top': props.scrollTop,
        'scroll-left': props.scrollLeft,
        'scroll-with-animation': props.scrollWithAnimation,
        'lower-threshold': props.lowerThreshold,
        'upper-threshold': props.upperThreshold,
        'show-scrollbar': props.showScrollbar,
      };

      // 仅 LiteApp 支持的属性
      if (props.scrollConfig) {
        result['scroll-config'] = props.scrollConfig;
      }

      // 透传用户自定义类名和样式
      if (props.class) {
        result.class = props.class;
      }
      if (userStyle.value) {
        result.style = userStyle.value;
      }

      // 透传其他未声明的属性
      return {
        ...result,
        ...attrs,
      };
    });

    // H5: 转换为 CSS 样式（最小封装）
    const scrollStyle = computed(() => {
      if (isLiteApp.value) return {};

      const baseStyle: Record<string, any> = {
        overflowX: props.scrollX ? 'auto' : 'hidden',
        overflowY: props.scrollY ? 'auto' : 'hidden',
        scrollBehavior: props.scrollWithAnimation ? 'smooth' : 'auto',
        WebkitOverflowScrolling: 'touch', // iOS 平滑滚动
      };

      // 合并用户自定义样式
      if (userStyle.value) {
        if (typeof userStyle.value === 'string') {
          // 字符串样式需要特殊处理，这里先合并到 style 对象
          return baseStyle;
        }
        return {
          ...baseStyle,
          ...userStyle.value,
        };
      }

      return baseStyle;
    });

    // H5: 类名处理
    const scrollClass = computed(() => {
      if (isLiteApp.value) return '';
      return classes(n(), props.class);
    });

    // H5: 通过 watch 监听 scrollTop/scrollLeft 变化，使用 DOM 属性设置
    watch(
      [() => props.scrollTop, () => props.scrollLeft],
      ([top, left]) => {
        if (!isLiteApp.value && scrollRef.value) {
          if (top !== undefined && top !== null) {
            const topValue = typeof top === 'string' ? parseFloat(top) : top;
            if (!isNaN(topValue)) {
              scrollRef.value.scrollTop = topValue;
            }
          }
          if (left !== undefined && left !== null) {
            const leftValue = typeof left === 'string' ? parseFloat(left) : left;
            if (!isNaN(leftValue)) {
              scrollRef.value.scrollLeft = leftValue;
            }
          }
        }
      },
      { immediate: true },
    );

    // H5: 阈值事件状态跟踪（避免重复触发）
    const lastScrollTop = ref(0);
    const lastScrollLeft = ref(0);
    const hasTriggeredUpper = ref(false);
    const hasTriggeredLower = ref(false);

    // H5: 适配原生 DOM 事件为统一格式
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = target;

      // 统一事件格式：转换为 event.detail 格式
      const scrollEvent = {
        detail: {
          scrollTop,
          scrollLeft,
          scrollHeight,
          scrollWidth,
        },
      };

      emit('scroll', scrollEvent);

      // 手动计算阈值，触发 scrolltoupper/scrolltolower（单向触发）
      const currentScrollTop = scrollTop;
      const scrollDirection = currentScrollTop > lastScrollTop.value ? 'down' : 'up';

      // 重置状态（当滚动方向改变时）
      if (scrollDirection === 'down' && lastScrollTop.value <= props.upperThreshold) {
        hasTriggeredUpper.value = false;
      }
      if (scrollDirection === 'up' && currentScrollTop + clientHeight >= scrollHeight - props.lowerThreshold) {
        hasTriggeredLower.value = false;
      }

      // 触发 scrolltoupper（滚动到顶部）
      if (currentScrollTop <= props.upperThreshold && !hasTriggeredUpper.value) {
        emit('scrolltoupper', { detail: { direction: 'top' } });
        hasTriggeredUpper.value = true;
      }

      // 触发 scrolltolower（滚动到底部）
      if (currentScrollTop + clientHeight >= scrollHeight - props.lowerThreshold && !hasTriggeredLower.value) {
        emit('scrolltolower', { detail: { direction: 'bottom' } });
        hasTriggeredLower.value = true;
      }

      // 横向滚动阈值检测
      if (props.scrollX) {
        const currentScrollLeft = scrollLeft;
        const scrollDirectionX = currentScrollLeft > lastScrollLeft.value ? 'right' : 'left';

        // 重置状态（当滚动方向改变时）
        if (scrollDirectionX === 'right' && lastScrollLeft.value <= props.upperThreshold) {
          hasTriggeredUpper.value = false;
        }
        if (scrollDirectionX === 'left' && currentScrollLeft + clientWidth >= scrollWidth - props.lowerThreshold) {
          hasTriggeredLower.value = false;
        }

        // 触发 scrolltoupper（滚动到左边）
        if (currentScrollLeft <= props.upperThreshold && !hasTriggeredUpper.value) {
          emit('scrolltoupper', { detail: { direction: 'left' } });
          hasTriggeredUpper.value = true;
        }

        // 触发 scrolltolower（滚动到右边）
        if (currentScrollLeft + clientWidth >= scrollWidth - props.lowerThreshold && !hasTriggeredLower.value) {
          emit('scrolltolower', { detail: { direction: 'right' } });
          hasTriggeredLower.value = true;
        }

        lastScrollLeft.value = currentScrollLeft;
      }

      lastScrollTop.value = currentScrollTop;
    };

    // LiteApp: 事件处理（直接使用 scroll-view 的事件）
    const handleScrollLiteApp = (event: any) => {
      // scroll-view 事件已经是 event.detail 格式，直接透传
      emit('scroll', event);
    };

    const handleScrollToUpperLiteApp = (event: any) => {
      emit('scrolltoupper', event);
    };

    const handleScrollToLowerLiteApp = (event: any) => {
      emit('scrolltolower', event);
    };

    const handleScrollToThreshold = (event: any) => {
      // 仅 LiteApp 支持
      emit('scrolltothreshold', event);
    };

    const handleScrollEnd = (event: any) => {
      // 仅 LiteApp 支持
      emit('scrollend', event);
    };

    // LiteApp: 事件监听器
    const scrollViewListeners = computed(() => {
      if (!isLiteApp.value) return {};

      return {
        scroll: handleScrollLiteApp,
        scrolltoupper: handleScrollToUpperLiteApp,
        scrolltolower: handleScrollToLowerLiteApp,
        scrolltothreshold: handleScrollToThreshold,
        scrollend: handleScrollEnd,
      };
    });

    // H5: 绑定原生事件
    const divListeners = computed(() => {
      if (isLiteApp.value) return {};

      return {
        scroll: handleScroll,
      };
    });

    // H5: 处理 div 的其他属性（排除事件和已处理的属性）
    const divAttrs = computed(() => {
      if (isLiteApp.value) return {};

      // 排除已处理的属性（class、style）和事件处理器，透传其他属性
      const result: Record<string, any> = {};
      Object.keys(attrs).forEach((key) => {
        if (!key.startsWith('on') && !key.startsWith('@') && key !== 'class' && key !== 'style') {
          result[key] = (attrs as any)[key];
        }
      });
      return result;
    });

    // 滚动到指定位置
    const scrollTo = (options: ScrollToOptions) => {
      if (isLiteApp.value) {
        // LiteApp: 使用 scroll-view 的 scrollTo 方法
        if (scrollViewRef.value && typeof scrollViewRef.value.scrollTo === 'function') {
          scrollViewRef.value.scrollTo({
            left: options.left,
            top: options.top,
            behavior: options.behavior || 'auto',
            duration: options.duration,
          });
        }
      } else {
        // H5: 使用原生 scrollTo 方法
        if (scrollRef.value && typeof scrollRef.value.scrollTo === 'function') {
          scrollRef.value.scrollTo({
            top: options.top ?? 0,
            left: options.left ?? 0,
            behavior: options.behavior || 'auto',
          });
        }
      }
    };

    // 从当前位置滚动偏移值（仅 LiteApp，H5 手动实现）
    const scrollBy = (options: ScrollByOptions) => {
      if (isLiteApp.value) {
        // LiteApp: 使用 scroll-view 的 scrollBy 方法
        if (scrollViewRef.value && typeof scrollViewRef.value.scrollBy === 'function') {
          scrollViewRef.value.scrollBy(options);
        }
      } else {
        // H5: 手动计算偏移量
        if (scrollRef.value) {
          const currentTop = scrollRef.value.scrollTop;
          const currentLeft = scrollRef.value.scrollLeft;
          scrollTo({
            top: currentTop + (options.top ?? 0),
            left: currentLeft + (options.left ?? 0),
            behavior: options.behavior,
          });
        }
      }
    };

    // 暴露方法（在 Vue 2/3 兼容模式下，直接返回即可）
    return {
      scrollTo,
      scrollBy,
      scrollRef,
      scrollViewRef,
      isLiteApp,
      scrollViewAttrs,
      scrollViewListeners,
      divAttrs,
      divListeners,
      scrollClass,
      scrollStyle,
      showScrollbar: computed(() => props.showScrollbar),
    };
  },
});
</script>

<style lang="less">
@import './scroll-view.less';
</style>
